import { createFileRoute, Link } from "@tanstack/react-router";
import {
	Bot,
	CalendarClock,
	GitBranch,
	MessageSquare,
	Sparkles,
	Zap,
} from "lucide-react";
import { FeatureGrid } from "@/components/feature-grid";
import { MarketingHero } from "@/components/marketing-hero";
import { MarketingPageShell } from "@/components/marketing-page-shell";
import { MarketingSection } from "@/components/marketing-section";
import { buildPageHead } from "@/lib/seo-meta";

const path = "/features/ai-scheduling";

export const Route = createFileRoute(path)({
	component: AiSchedulingFeature,
	head: ({ matches }) =>
		buildPageHead({
			matches,
			internalPath: path,
			title: "AgentReacher | AI Scheduling",
			description:
				"AgentReacher's AI scheduling drafts, schedules, and publishes social media posts in your voice. Across every connected platform, from one calendar.",
		}),
});

function AiSchedulingFeature() {
	return (
		<MarketingPageShell>
			<MarketingHero
				eyebrow="Feature: AI scheduling"
				title="AI scheduling that actually understands your voice."
				description="The agent drafts in your tone, schedules into the right windows, and publishes across every connected channel. You stay in control."
			/>

			<MarketingSection
				title="What AI scheduling means at AgentReacher"
				description="More than auto-posting. The agent owns the full lifecycle from draft to publish."
			>
				<FeatureGrid
					features={[
						{
							icon: Bot,
							title: "Voice training",
							description:
								"Drop in 5–10 past posts. The agent matches your tone, hooks, and structure.",
						},
						{
							icon: Sparkles,
							title: "Per-platform variations",
							description:
								"One idea, rewritten for each network. LinkedIn long-form, X thread, Bluesky native.",
						},
						{
							icon: CalendarClock,
							title: "Audience-aware timing",
							description:
								"Slots picked for when your audience is online, not generic peak hours.",
						},
						{
							icon: GitBranch,
							title: "Every platform",
							description:
								"Bluesky, X, Threads, LinkedIn, Instagram, TikTok, YouTube, Facebook, Pinterest.",
						},
						{
							icon: MessageSquare,
							title: "Approval gates",
							description:
								"Reviewer roles built in. No post ships without explicit approval.",
						},
						{
							icon: Zap,
							title: "Drive from chat",
							description:
								"MCP integration with Claude, ChatGPT, and Cursor. Schedule from your assistant.",
						},
					]}
				/>
			</MarketingSection>

			<MarketingSection>
				<div className="mx-auto max-w-2xl space-y-4 text-center text-sm leading-relaxed text-muted-foreground">
					<p>
						See related features:{" "}
						<Link
							to="/features/cross-posting"
							className="text-primary transition-colors hover:text-primary/80"
						>
							cross-posting
						</Link>
						,{" "}
						<Link
							to="/features/calendar"
							className="text-primary transition-colors hover:text-primary/80"
						>
							unified calendar
						</Link>
						, and{" "}
						<Link
							to="/features/team-approval"
							className="text-primary transition-colors hover:text-primary/80"
						>
							team approvals
						</Link>
						.
					</p>
				</div>
			</MarketingSection>
		</MarketingPageShell>
	);
}
